import {
  buildAdmissionsTuitionApprovalVersion,
  isAdmissionsTuitionConfigurationApproved,
  type AdmissionsTuitionContractVersion,
  type AdmissionsTuitionContractSnapshot,
} from '@triedge/students-families-domain';
import type {
  AdmissionsTuitionChildConfiguration,
  AdmissionsTuitionConfiguration,
  AdmissionsTuitionFeeLine,
  AdmissionsTuitionMoneyField,
  AdmissionsTuitionPercentDiscount,
  AdmissionsTuitionResolution,
} from '@triedge/students-families-domain';
import type {
  TuitionPaymentPlan,
  TuitionRateCategory,
  TuitionRateCategoryFee,
} from '@triedge/staff-settings-domain';

const DEFAULT_STAFF_DISCOUNT_BPS = 2500;
const DEFAULT_CHINUCH_DISCOUNT_BPS = 3500;
const COMBINED_DISCOUNT_CAP_BPS = 5000;

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const normalizeNullableText = (value: unknown): string | null => {
  const normalized = normalizeText(value);
  return normalized.length > 0 ? normalized : null;
};

const clampCents = (value: number): number =>
  Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;

const clone = <T>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

const getContractHistoryForInvalidation = (
  previous?: AdmissionsTuitionContractSnapshot | null,
): AdmissionsTuitionContractVersion[] => {
  const versions = [...(previous?.versions ?? [])];
  const version = normalizeText(previous?.version);
  const documentId = normalizeText(previous?.documentId);
  if (version && documentId && !versions.some((entry) => entry.version === version)) {
    versions.push({
      id: version,
      version,
      status: previous?.status === 'sent' ? 'sent' : 'draft',
      documentId,
      resourceId: normalizeNullableText(previous?.resourceId),
      generatedAt: normalizeNullableText(previous?.generatedAt),
      sentAt: normalizeNullableText(previous?.sentAt),
      signedAt: null,
      signedDocumentId: null,
      signedDocumentResourceId: null,
      signerFullName: null,
      supersedesVersion: null,
      contractModel: null,
    });
  }
  return versions;
};

const unapprovedContract = (
  previous?: AdmissionsTuitionContractSnapshot | null,
): AdmissionsTuitionContractSnapshot => ({
  ...previous,
  status: 'not_generated',
  documentId: null,
  resourceId: null,
  generatedAt: null,
  sentAt: null,
  version: null,
  versions: getContractHistoryForInvalidation(previous),
});

export const invalidateAdmissionsTuitionApproval = (
  configuration: AdmissionsTuitionConfiguration,
): AdmissionsTuitionConfiguration => ({
  ...configuration,
  contract: unapprovedContract(configuration.contract),
});

export const approveAdmissionsTuitionConfiguration = (
  configuration: AdmissionsTuitionConfiguration,
  options: {
    approvedAt?: string;
  } = {},
): AdmissionsTuitionConfiguration => {
  if (isAdmissionsTuitionConfigurationApproved(configuration)) {
    return configuration;
  }
  const approvedAt = options.approvedAt ?? new Date().toISOString();
  return {
    ...configuration,
    updatedAt: approvedAt,
    contract: {
      ...unapprovedContract(configuration.contract),
      version: buildAdmissionsTuitionApprovalVersion(configuration),
    },
  };
};

export { buildAdmissionsTuitionApprovalVersion, isAdmissionsTuitionConfigurationApproved };

const roundPercentAmount = (baseCents: number, basisPoints: number): number =>
  Math.round((clampCents(baseCents) * Math.max(0, basisPoints)) / 10000);

const isMoneyFieldOverridden = (
  field: Pick<AdmissionsTuitionMoneyField, 'overridden' | 'currentCents' | 'defaultCents'> | null | undefined,
): boolean =>
  Boolean(field?.overridden)
  || clampCents(field?.currentCents ?? 0) !== clampCents(field?.defaultCents ?? 0);

const moneyField = (
  currentCents: number,
  defaultCents: number,
  overridden = false,
  note?: string | null,
): AdmissionsTuitionMoneyField => ({
  currentCents: clampCents(currentCents),
  defaultCents: clampCents(defaultCents),
  overridden: overridden || clampCents(currentCents) !== clampCents(defaultCents),
  note: note ?? null,
});

const discountField = (input: {
  eligible: boolean;
  enabled?: boolean;
  percentBasisPoints?: number;
  defaultPercentBasisPoints?: number;
  overridden?: boolean;
  amountCents?: number;
  defaultAmountCents?: number;
}): AdmissionsTuitionPercentDiscount => ({
  eligible: input.eligible,
  enabled: Boolean(input.eligible && input.enabled),
  percentBasisPoints: input.eligible ? Math.max(0, Math.round(input.percentBasisPoints ?? 0)) : 0,
  defaultPercentBasisPoints: input.eligible
    ? Math.max(0, Math.round(input.defaultPercentBasisPoints ?? 0))
    : 0,
  overridden: Boolean(input.overridden),
  amountCents: clampCents(input.amountCents ?? 0),
  defaultAmountCents: clampCents(input.defaultAmountCents ?? 0),
});

const isPerFamilyFee = (fee: TuitionRateCategoryFee): boolean => fee.amountType === 'per_family';

const isFeeExempt = (fee: TuitionRateCategoryFee, gradeKey: string): boolean =>
  fee.exemptGradeKeys.includes(gradeKey);

const isConditionalLateFee = (fee: TuitionRateCategoryFee): boolean => {
  if (fee.conditional === true) {
    return true;
  }
  const token = normalizeText(`${fee.id} ${fee.name} ${fee.entryType}`)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_');
  return token.includes('late_registration') || token.includes('scholarship_late');
};

const resolveAllocationChildByFeeId = (
  children: AdmissionsTuitionResolution['acceptedChildren'],
  feeSchedule: TuitionRateCategory['feeSchedule'],
): Map<string, string> => {
  const allocation = new Map<string, string>();
  for (const fee of feeSchedule) {
    if (!isPerFamilyFee(fee)) {
      continue;
    }
    const allocatedChild = children.find((child) => !isFeeExempt(fee, child.gradeKey));
    if (allocatedChild) {
      allocation.set(fee.id, allocatedChild.childApplicationId);
    }
  }
  return allocation;
};

const resolveDefaultTuitionCents = (
  category: TuitionRateCategory,
  child: AdmissionsTuitionResolution['acceptedChildren'][number],
): number => {
  if (category.flatRateMode && category.flatRateAmount != null) {
    return clampCents(category.flatRateAmount * 100);
  }

  const directMatch = category.tuitionRates.find((rate) =>
    rate.gradeKeys.includes(child.gradeKey)
    && (rate.gender === 'all' || normalizeText(rate.gender) === normalizeText(child.gender || 'all'))
  );
  if (directMatch) {
    return clampCents(directMatch.amount * 100);
  }

  const fallbackMatch = category.tuitionRates.find((rate) => rate.gradeKeys.includes(child.gradeKey));
  return clampCents((fallbackMatch?.amount ?? 0) * 100);
};

const buildDefaultFeeLines = (
  children: AdmissionsTuitionResolution['acceptedChildren'],
  category: TuitionRateCategory,
): Map<string, AdmissionsTuitionFeeLine[]> => {
  const byChildId = new Map<string, AdmissionsTuitionFeeLine[]>();
  const allocationByFeeId = resolveAllocationChildByFeeId(children, category.feeSchedule);

  for (const child of children) {
    const feeLines = category.feeSchedule.map((fee) => {
      const exempt = isFeeExempt(fee, child.gradeKey);
      const allocatedChildId = allocationByFeeId.get(fee.id);
      const allocatedHere = isPerFamilyFee(fee)
        ? allocatedChildId === child.childApplicationId
        : true;
      const includedInChildName = isPerFamilyFee(fee) && !allocatedHere
        ? children.find((candidate) => candidate.childApplicationId === allocatedChildId)?.name ?? null
        : null;
      const conditionalLateFee = isConditionalLateFee(fee);
      const defaultAmountCents = exempt || !allocatedHere || conditionalLateFee
        ? 0
        : clampCents(fee.amount * 100);
      const defaultDueDate = normalizeNullableText(fee.dueDate);
      return {
        id: fee.id,
        name: fee.name,
        rateDescription: fee.rateDescription,
        amount: moneyField(
          defaultAmountCents,
          defaultAmountCents,
          false,
          conditionalLateFee
            ? 'Conditional fee; set an amount only if this family qualifies.'
            : exempt
              ? 'Exempt'
              : null,
        ),
        dueDate: defaultDueDate,
        defaultDueDate,
        exemptReason: exempt ? `${child.gradeLabel} is exempt for this fee.` : null,
        perFamilyAllocatedHere: Boolean(isPerFamilyFee(fee) && allocatedHere && !exempt),
        includedInChildName,
      } satisfies AdmissionsTuitionFeeLine;
    });
    byChildId.set(child.childApplicationId, feeLines);
  }

  return byChildId;
};

const resolveMultiChildDiscountDefaultCents = (
  child: AdmissionsTuitionResolution['acceptedChildren'][number],
  category: TuitionRateCategory,
  eligibleChildCount: number,
): number => {
  if (!category.multiChildDiscountEligible) {
    return 0;
  }
  if (child.gradeKey === 'nursery' || child.gradeKey === 'kindergarten') {
    return 0;
  }
  if (eligibleChildCount >= 3) {
    return 50000;
  }
  if (eligibleChildCount === 2) {
    return 25000;
  }
  return 0;
};

const applyMoneyFieldOverride = (
  previous: AdmissionsTuitionMoneyField | null | undefined,
  defaultCents: number,
  note?: string | null,
): AdmissionsTuitionMoneyField => {
  const overridden = isMoneyFieldOverridden(previous);
  return moneyField(
    overridden ? clampCents(previous?.currentCents ?? defaultCents) : defaultCents,
    defaultCents,
    overridden,
    note ?? previous?.note ?? null,
  );
};

const applyDiscountOverride = (
  previous: AdmissionsTuitionPercentDiscount | null | undefined,
  input: {
    eligible: boolean;
    defaultPercentBasisPoints: number;
    baseTuitionCents: number;
  },
): AdmissionsTuitionPercentDiscount => {
  if (!input.eligible) {
    return discountField({ eligible: false });
  }

  const overridden =
    Boolean(previous?.overridden)
    || Boolean(previous?.enabled)
    || Math.max(0, Math.round(previous?.percentBasisPoints ?? 0)) !== input.defaultPercentBasisPoints;
  const enabled = Boolean(previous?.enabled);
  const percentBasisPoints = enabled
    ? Math.max(0, Math.round(previous?.percentBasisPoints ?? input.defaultPercentBasisPoints))
    : Math.max(0, Math.round(previous?.percentBasisPoints ?? input.defaultPercentBasisPoints));
  const amountCents = enabled
    ? roundPercentAmount(input.baseTuitionCents, percentBasisPoints)
    : 0;
  const defaultAmountCents = roundPercentAmount(
    input.baseTuitionCents,
    input.defaultPercentBasisPoints,
  );

  return discountField({
    eligible: true,
    enabled,
    percentBasisPoints,
    defaultPercentBasisPoints: input.defaultPercentBasisPoints,
    overridden,
    amountCents,
    defaultAmountCents,
  });
};

const recalculateChild = (
  child: AdmissionsTuitionChildConfiguration,
  category: TuitionRateCategory,
  eligibleChildCount: number,
): AdmissionsTuitionChildConfiguration => {
  const next = clone(child);
  const baseTuitionCents = clampCents(next.baseTuition.currentCents);
  const defaultMultiChildCents = resolveMultiChildDiscountDefaultCents(
    {
      childApplicationId: next.childApplicationId,
      studentId: next.studentId ?? null,
      name: next.name,
      gradeLabel: next.gradeLabel,
      gradeKey: next.gradeKey,
      divisionLabel: next.divisionLabel,
      gender: next.gender ?? null,
      status: next.status ?? null,
      sortOrder: 0,
    },
    category,
    eligibleChildCount,
  );
  next.multiChildDiscount = category.multiChildDiscountEligible
    ? applyMoneyFieldOverride(next.multiChildDiscount, defaultMultiChildCents)
    : moneyField(0, 0, false, 'Not eligible');

  next.staffDiscount = applyDiscountOverride(next.staffDiscount, {
    eligible: category.staffDiscountEligible,
    defaultPercentBasisPoints: DEFAULT_STAFF_DISCOUNT_BPS,
    baseTuitionCents,
  });
  next.chinuchDiscount = applyDiscountOverride(next.chinuchDiscount, {
    eligible: category.chinuchDiscountEligible,
    defaultPercentBasisPoints: DEFAULT_CHINUCH_DISCOUNT_BPS,
    baseTuitionCents,
  });

  const staffEnabled = next.staffDiscount.eligible && next.staffDiscount.enabled;
  const chinuchEnabled = next.chinuchDiscount.eligible && next.chinuchDiscount.enabled;
  if (staffEnabled && chinuchEnabled) {
    const combinedPercentBasisPoints = Math.min(
      COMBINED_DISCOUNT_CAP_BPS,
      next.staffDiscount.percentBasisPoints + next.chinuchDiscount.percentBasisPoints,
    );
    const combinedAmountCents = roundPercentAmount(baseTuitionCents, combinedPercentBasisPoints);
    next.combinedDiscount = moneyField(
      combinedAmountCents,
      combinedAmountCents,
      true,
      'Combined staff/chinuch discount capped at 50%.',
    );
    next.staffDiscount.amountCents = 0;
    next.chinuchDiscount.amountCents = 0;
  } else {
    next.staffDiscount.amountCents = staffEnabled
      ? roundPercentAmount(baseTuitionCents, next.staffDiscount.percentBasisPoints)
      : 0;
    next.chinuchDiscount.amountCents = chinuchEnabled
      ? roundPercentAmount(baseTuitionCents, next.chinuchDiscount.percentBasisPoints)
      : 0;
    next.combinedDiscount = moneyField(0, 0, false, null);
  }

  if (!category.financialAidEligible) {
    next.financialAidCredit = moneyField(0, 0, false, 'Not eligible');
  }

  if (!category.fundraisingObligationEnabled) {
    next.fundraising = {
      enabled: false,
      exempt: false,
      amount: moneyField(0, 0, false, null),
    };
  } else {
    const defaultFundraisingCents = clampCents(category.fundraisingAmountPerChild * 100);
    const amountField = applyMoneyFieldOverride(next.fundraising.amount, defaultFundraisingCents);
    next.fundraising = {
      enabled: true,
      exempt: Boolean(next.fundraising.exempt),
      amount: next.fundraising.exempt
        ? moneyField(0, defaultFundraisingCents, Boolean(amountField.overridden), null)
        : amountField,
    };
  }

  const feeTotalCents = next.fees.reduce((sum, fee) => sum + clampCents(fee.amount.currentCents), 0);
  const staffChinuchDiscountCents = clampCents(next.combinedDiscount.currentCents)
    + clampCents(next.staffDiscount.amountCents)
    + clampCents(next.chinuchDiscount.amountCents);
  const subtotalCents = Math.max(
    baseTuitionCents
      + feeTotalCents
      - clampCents(next.multiChildDiscount.currentCents)
      - staffChinuchDiscountCents
      - clampCents(next.financialAidCredit.currentCents),
    0,
  );
  const defaultSubtotalCents = Math.max(
    clampCents(next.baseTuition.defaultCents)
      + next.fees.reduce((sum, fee) => sum + clampCents(fee.amount.defaultCents), 0)
      - clampCents(next.multiChildDiscount.defaultCents)
      - clampCents(next.staffDiscount.defaultAmountCents)
      - clampCents(next.chinuchDiscount.defaultAmountCents)
      - clampCents(next.financialAidCredit.defaultCents),
    0,
  );

  next.subtotalCents = subtotalCents;
  next.defaultSubtotalCents = defaultSubtotalCents;
  return next;
};

const findRateCategory = (
  categories: TuitionRateCategory[],
  rateCategoryId: string,
): TuitionRateCategory | null =>
  categories.find((category) => category.id === rateCategoryId) ?? null;

const findPaymentPlan = (
  paymentPlans: TuitionPaymentPlan[],
  paymentPlanId: string | null | undefined,
): TuitionPaymentPlan | null =>
  paymentPlanId
    ? paymentPlans.find((plan) => plan.enabled && plan.id === paymentPlanId) ?? null
    : null;

const findDefaultPaymentPlanStartDate = (
  paymentPlans: TuitionPaymentPlan[],
): string | null =>
  normalizeNullableText(paymentPlans.find((plan) => plan.enabled)?.startDate);

const buildPaymentPreview = (
  paymentPlanId: string | null | undefined,
  paymentPlans: TuitionPaymentPlan[],
  totalObligationCents: number,
): AdmissionsTuitionConfiguration['paymentPlan'] => {
  const plan = findPaymentPlan(paymentPlans, paymentPlanId);
  if (!plan || plan.installmentCount <= 0) {
    return {
      paymentPlanId: null,
      paymentPlanLabel: null,
      installmentCount: 0,
      installmentAmountCents: 0,
      finalInstallmentAmountCents: 0,
      startDate: null,
      endDate: null,
      previewLabel: null,
    };
  }

  const installmentCount = plan.installmentCount;
  const installmentAmountCents = Math.floor(totalObligationCents / installmentCount);
  const finalInstallmentAmountCents =
    totalObligationCents - installmentAmountCents * (installmentCount - 1);
  const formatCurrency = (cents: number): string =>
    `$${(cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  const previewLabel =
    installmentAmountCents === finalInstallmentAmountCents
      ? `${installmentCount} payments of ${formatCurrency(installmentAmountCents)} from ${plan.startDate} to ${plan.endDate}`
      : `${installmentCount} payments of ${formatCurrency(installmentAmountCents)} with a final payment of ${formatCurrency(finalInstallmentAmountCents)} from ${plan.startDate} to ${plan.endDate}`;

  return {
    paymentPlanId: plan.id,
    paymentPlanLabel: plan.label,
    installmentCount,
    installmentAmountCents,
    finalInstallmentAmountCents,
    startDate: plan.startDate,
    endDate: plan.endDate,
    previewLabel,
  };
};

const buildCategoryFeeDueDateLookup = (
  category: TuitionRateCategory,
): Map<string, string | null> => {
  const lookup = new Map<string, string | null>();
  for (const fee of category.feeSchedule) {
    lookup.set(fee.id, normalizeNullableText(fee.dueDate));
  }
  return lookup;
};

const applyPaymentPlanDueDateDefaults = (
  children: AdmissionsTuitionChildConfiguration[],
  category: TuitionRateCategory,
  paymentPlanStartDate: string | null | undefined,
): AdmissionsTuitionChildConfiguration[] => {
  const categoryDueDateByFeeId = buildCategoryFeeDueDateLookup(category);
  const paymentPlanDefaultDueDate = normalizeNullableText(paymentPlanStartDate);

  return children.map((child) => ({
    ...child,
    fees: child.fees.map((fee) => {
      const categoryDueDate = categoryDueDateByFeeId.get(fee.id) ?? null;
      const nextDefaultDueDate = categoryDueDate ?? paymentPlanDefaultDueDate;
      const currentDueDate = normalizeNullableText(fee.dueDate);
      const previousDefaultDueDate = normalizeNullableText(fee.defaultDueDate);
      const dueDateOverridden = currentDueDate != null
        && (
          previousDefaultDueDate != null
            ? currentDueDate !== previousDefaultDueDate
            : currentDueDate !== nextDefaultDueDate
        );

      return {
        ...fee,
        defaultDueDate: nextDefaultDueDate,
        dueDate: dueDateOverridden ? currentDueDate : nextDefaultDueDate,
      };
    }),
  }));
};

export const recalculateAdmissionsTuitionConfiguration = (
  configuration: AdmissionsTuitionConfiguration,
  categories: TuitionRateCategory[],
  paymentPlans: TuitionPaymentPlan[],
): AdmissionsTuitionConfiguration => {
  const category = findRateCategory(categories, configuration.rateCategoryId);
  if (!category) {
    return configuration;
  }

  const eligibleChildCount = configuration.children.filter(
    (child) => child.gradeKey !== 'nursery' && child.gradeKey !== 'kindergarten',
  ).length;
  const children = configuration.children.map((child) =>
    recalculateChild(child, category, eligibleChildCount),
  );

  const tuitionSubtotalCents = children.reduce((sum, child) => sum + clampCents(child.baseTuition.currentCents), 0);
  const multiChildDiscountCents = children.reduce((sum, child) => sum + clampCents(child.multiChildDiscount.currentCents), 0);
  const staffChinuchDiscountCents = children.reduce(
    (sum, child) =>
      sum
      + clampCents(child.combinedDiscount.currentCents)
      + clampCents(child.staffDiscount.amountCents)
      + clampCents(child.chinuchDiscount.amountCents),
    0,
  );
  const totalTuitionCents = Math.max(
    tuitionSubtotalCents - multiChildDiscountCents - staffChinuchDiscountCents,
    0,
  );
  const totalFeesCents = children.reduce(
    (sum, child) =>
      sum + child.fees.reduce((childSum, fee) => childSum + clampCents(fee.amount.currentCents), 0),
    0,
  );
  const financialAidCreditCents = children.reduce(
    (sum, child) => sum + clampCents(child.financialAidCredit.currentCents),
    0,
  );
  const fundraisingObligationCents = children.reduce(
    (sum, child) =>
      sum
      + (
        child.fundraising.enabled && !child.fundraising.exempt
          ? clampCents(child.fundraising.amount.currentCents)
          : 0
      ),
    0,
  );
  const totalObligationCents = Math.max(
    totalTuitionCents + totalFeesCents - financialAidCreditCents + fundraisingObligationCents,
    0,
  );
  const paymentPlan = buildPaymentPreview(
    configuration.paymentPlan.paymentPlanId,
    paymentPlans,
    totalObligationCents,
  );
  const childrenWithDueDateDefaults = applyPaymentPlanDueDateDefaults(
    children,
    category,
    paymentPlan.startDate ?? findDefaultPaymentPlanStartDate(paymentPlans),
  );

  return {
    ...configuration,
    rateCategoryLabel: category.label,
    children: childrenWithDueDateDefaults,
    totals: {
      tuitionSubtotalCents,
      multiChildDiscountCents,
      staffChinuchDiscountCents,
      totalTuitionCents,
      totalFeesCents,
      financialAidCreditCents,
      fundraisingObligationCents,
      totalObligationCents,
    },
    paymentPlan,
    configurationComplete:
      Boolean(paymentPlan.paymentPlanId)
      && children.some((child) => clampCents(child.baseTuition.currentCents) > 0),
  };
};

export const buildAdmissionsTuitionConfiguration = (input: {
  resolution: AdmissionsTuitionResolution;
  categories: TuitionRateCategory[];
  paymentPlans: TuitionPaymentPlan[];
  defaultRateCategoryId?: string | null;
  previousConfiguration?: AdmissionsTuitionConfiguration | null;
}): AdmissionsTuitionConfiguration => {
  const previous = input.previousConfiguration ?? null;
  const fallbackCategory =
    findRateCategory(input.categories, normalizeText(previous?.rateCategoryId))
    ?? findRateCategory(input.categories, normalizeText(input.defaultRateCategoryId))
    ?? input.categories[0]
    ?? null;

  if (!fallbackCategory) {
    return {
      version: '1',
      familyId: input.resolution.family.id,
      applicationId: input.resolution.applicationId,
      academicYear: input.resolution.academicYear,
      rateCategoryId: 'unconfigured',
      rateCategoryLabel: 'Unconfigured',
      admissionsSource: true,
      configurationComplete: false,
      updatedAt: previous?.updatedAt ?? null,
      children: [],
      totals: {
        tuitionSubtotalCents: 0,
        multiChildDiscountCents: 0,
        staffChinuchDiscountCents: 0,
        totalTuitionCents: 0,
        totalFeesCents: 0,
        financialAidCreditCents: 0,
        fundraisingObligationCents: 0,
        totalObligationCents: 0,
      },
      paymentPlan: buildPaymentPreview(null, input.paymentPlans, 0),
      contract: previous?.contract ?? unapprovedContract(),
    };
  }

  const defaultFeeLinesByChildId = buildDefaultFeeLines(
    input.resolution.acceptedChildren,
    fallbackCategory,
  );

  const children: AdmissionsTuitionChildConfiguration[] = input.resolution.acceptedChildren.map((resolvedChild) => {
    const previousChild = previous?.children.find(
      (child) => child.childApplicationId === resolvedChild.childApplicationId,
    );
    const defaultBaseTuitionCents = resolveDefaultTuitionCents(fallbackCategory, resolvedChild);
    const baseTuition = applyMoneyFieldOverride(previousChild?.baseTuition, defaultBaseTuitionCents);
    const defaultFees = defaultFeeLinesByChildId.get(resolvedChild.childApplicationId) ?? [];
    const fees = defaultFees.map((defaultFee) => {
      const previousFee = previousChild?.fees.find((fee) => fee.id === defaultFee.id);
      const amount = applyMoneyFieldOverride(
        previousFee?.amount,
        defaultFee.amount.defaultCents,
        defaultFee.amount.note ?? defaultFee.exemptReason,
      );
      const dueDateOverridden =
        normalizeText(previousFee?.dueDate) !== normalizeText(previousFee?.defaultDueDate)
        && normalizeText(previousFee?.dueDate).length > 0;
      return {
        ...defaultFee,
        amount,
        dueDate: dueDateOverridden ? previousFee?.dueDate ?? defaultFee.dueDate : defaultFee.dueDate,
      };
    });
    const financialAidCredit = fallbackCategory.financialAidEligible
      ? applyMoneyFieldOverride(previousChild?.financialAidCredit, 0)
      : moneyField(0, 0, false, 'Not eligible');
    const fundraisingDefaultCents = clampCents(fallbackCategory.fundraisingAmountPerChild * 100);
    const previousFundraising = previousChild?.fundraising;
    const fundraisingAmount = fallbackCategory.fundraisingObligationEnabled
      ? applyMoneyFieldOverride(previousFundraising?.amount, fundraisingDefaultCents)
      : moneyField(0, 0, false, null);

    return {
      childApplicationId: resolvedChild.childApplicationId,
      studentId: resolvedChild.studentId ?? null,
      name: resolvedChild.name,
      gradeLabel: resolvedChild.gradeLabel,
      gradeKey: resolvedChild.gradeKey,
      divisionLabel: resolvedChild.divisionLabel,
      gender: resolvedChild.gender ?? null,
      status: resolvedChild.status ?? null,
      baseTuition,
      fees,
      multiChildDiscount: moneyField(0, 0, false, null),
      staffDiscount: previousChild?.staffDiscount
        ? clone(previousChild.staffDiscount)
        : discountField({
            eligible: fallbackCategory.staffDiscountEligible,
            enabled: false,
            percentBasisPoints: DEFAULT_STAFF_DISCOUNT_BPS,
            defaultPercentBasisPoints: DEFAULT_STAFF_DISCOUNT_BPS,
          }),
      chinuchDiscount: previousChild?.chinuchDiscount
        ? clone(previousChild.chinuchDiscount)
        : discountField({
            eligible: fallbackCategory.chinuchDiscountEligible,
            enabled: false,
            percentBasisPoints: DEFAULT_CHINUCH_DISCOUNT_BPS,
            defaultPercentBasisPoints: DEFAULT_CHINUCH_DISCOUNT_BPS,
          }),
      combinedDiscount: moneyField(0, 0, false, null),
      financialAidCredit,
      fundraising: {
        enabled: fallbackCategory.fundraisingObligationEnabled,
        exempt: Boolean(previousFundraising?.exempt),
        amount: previousFundraising?.exempt
          ? moneyField(0, fundraisingDefaultCents, Boolean(fundraisingAmount.overridden), null)
          : fundraisingAmount,
      },
      subtotalCents: 0,
      defaultSubtotalCents: 0,
    };
  });

  return recalculateAdmissionsTuitionConfiguration(
    {
      version: '1',
      familyId: input.resolution.family.id,
      applicationId: input.resolution.applicationId,
      academicYear: input.resolution.academicYear,
      rateCategoryId: fallbackCategory.id,
      rateCategoryLabel: fallbackCategory.label,
      admissionsSource: true,
      configurationComplete: false,
      updatedAt: previous?.updatedAt ?? null,
      children,
      totals: {
        tuitionSubtotalCents: 0,
        multiChildDiscountCents: 0,
        staffChinuchDiscountCents: 0,
        totalTuitionCents: 0,
        totalFeesCents: 0,
        financialAidCreditCents: 0,
        fundraisingObligationCents: 0,
        totalObligationCents: 0,
      },
      paymentPlan: buildPaymentPreview(previous?.paymentPlan.paymentPlanId ?? null, input.paymentPlans, 0),
      contract: previous?.contract ?? unapprovedContract(),
    },
    input.categories,
    input.paymentPlans,
  );
};

export const updateAdmissionsTuitionRateCategory = (
  configuration: AdmissionsTuitionConfiguration,
  options: {
    resolution: AdmissionsTuitionResolution;
    categories: TuitionRateCategory[];
    paymentPlans: TuitionPaymentPlan[];
    rateCategoryId: string;
  },
): AdmissionsTuitionConfiguration => {
  const previous = {
    ...configuration,
    rateCategoryId: options.rateCategoryId,
  };
  return invalidateAdmissionsTuitionApproval(
    buildAdmissionsTuitionConfiguration({
      resolution: options.resolution,
      categories: options.categories,
      paymentPlans: options.paymentPlans,
      defaultRateCategoryId: options.rateCategoryId,
      previousConfiguration: previous,
    }),
  );
};

export const updateAdmissionsTuitionPaymentPlan = (
  configuration: AdmissionsTuitionConfiguration,
  paymentPlanId: string | null,
  categories: TuitionRateCategory[],
  paymentPlans: TuitionPaymentPlan[],
): AdmissionsTuitionConfiguration =>
  invalidateAdmissionsTuitionApproval(recalculateAdmissionsTuitionConfiguration(
    {
      ...configuration,
      paymentPlan: {
        ...configuration.paymentPlan,
        paymentPlanId,
      },
    },
    categories,
    paymentPlans,
  ));

export const formatTuitionCurrency = (cents: number): string =>
  `$${(clampCents(cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const parseCurrencyInputToCents = (value: string): number => {
  const normalized = String(value)
    .replace(/[^0-9.-]/g, '')
    .trim();
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return clampCents(parsed * 100);
};

export const updateAdmissionsTuitionConfiguration = (
  configuration: AdmissionsTuitionConfiguration,
  categories: TuitionRateCategory[],
  paymentPlans: TuitionPaymentPlan[],
  updater: (draft: AdmissionsTuitionConfiguration) => void,
): AdmissionsTuitionConfiguration => {
  const next = clone(configuration);
  updater(next);
  next.updatedAt = new Date().toISOString();
  return invalidateAdmissionsTuitionApproval(
    recalculateAdmissionsTuitionConfiguration(next, categories, paymentPlans),
  );
};

export const findAdmissionsTuitionChild = (
  configuration: AdmissionsTuitionConfiguration,
  childApplicationId: string,
): AdmissionsTuitionChildConfiguration | null =>
  configuration.children.find((child) => child.childApplicationId === childApplicationId) ?? null;

export const findAdmissionsTuitionFee = (
  child: AdmissionsTuitionChildConfiguration,
  feeId: string,
): AdmissionsTuitionFeeLine | null =>
  child.fees.find((fee) => fee.id === feeId) ?? null;
